import React from 'react';
import * as d3 from 'd3';
import { NodeGroup } from 'react-move';
import { Context } from '../../Context';
export default class RadialTidyTreeNode extends React.Component {
  concat = (str, length = 20) => {
    const substr = str.substring(0, length);
    return substr.length < str.length ? `${substr}...` : str;
  };
  constructTree = (treeBranch, childrenKey) => {
    const { bundleKey } = this.props;
    const key = bundleKey;

    const mainArray = treeBranch[childrenKey];

    const qScale = d3
      .scaleQuantile()
      .domain(mainArray.map((d) => d[key]))
      .range([0, 1, 2, 3]);

    const keyChecker = (leaf, key, invert = false) => {
      switch (key) {
        case 'amount':
          if (invert) {
            return qScale
              .invertExtent(qScale(leaf[key]))
              .map((dd, i) => {
                if (i === 0) {
                  return d3.format('$,')(dd, true);
                }
                return d3.format('$,')(dd, true);
              })
              .join('—');
          }
          return qScale(leaf[key]);
        default:
          return leaf[key];
      }
    };

    const children = [];

    mainArray.forEach((leaf) => {
      const k = keyChecker(leaf, key);
      const c = children.find((d) => d.name === k);
      if (!c) {
        children.push({
          name: k,
          committee_name: keyChecker(leaf, key, true),
          children: [leaf]
        });
      } else {
        c.children.push(leaf);
      }
    });
    return { ...treeBranch, children };
  };

  tree = (data, radians, radius) => {
    return d3
      .tree()
      .size([radians, radius])
      .separation((a, b) => (a.parent == b.parent ? 1 : 2) / a.depth)(
      d3.hierarchy(data)
    );
  };

  radialLink = d3
    .linkRadial()
    .angle((d) => d.x)
    .radius((d) => d.y);

  line = d3
    .line()
    .x((d) => {
      return d[0];
    })
    .y((d) => {
      return d[1];
    })
    .curve(d3.curveBasis);

  render() {
    const { radialLink } = this;
    const { w } = this.context;
    const { index, node, handleSelect, next: nextNode, zoomed } = this.props;
    const {
      data,
      state: {
        radians,
        radiusMultiplier: radMult,
        rotationMargin,
        xPos,
        xMid,
        maxRad
      }
    } = node;

    const radiusMultiplier = Math.min(radMult, 0.9);

    const rootSource = this.tree(
      this.constructTree(data, 'asSource'),
      Math.max(0, radians - rotationMargin * 2),
      maxRad * radiusMultiplier
    );
    const paths = rootSource.links().map((link) => {
      return { link, d: radialLink(link) };
    });

    const rootTarget = this.tree(
      this.constructTree(data, 'asTarget'),
      Math.min(-radians + rotationMargin * 2, 0),
      maxRad * radiusMultiplier
    );
    const paths2 = rootTarget.links().map((link) => {
      return { link, d: radialLink(link) };
    });

    const points = rootSource.descendants().reverse();
    const points2 = rootTarget.descendants().reverse();
    const leaves = points.filter((d) => d.depth > 0);
    const leaves2 = points2.filter((d) => d.depth > 0);

    let ndeg;
    let nx, ny;
    let pdeg;
    let px, py;
    const { treeNode } = data;
    if (treeNode.next) {
      const rotation = leaves.find((d) => d.data.id === treeNode.next).x;
      ndeg = rotation + rotationMargin;
      nx = Math.sin(ndeg) * maxRad * radiusMultiplier;
      ny = -Math.cos(ndeg) * maxRad * radiusMultiplier;
    }
    if (treeNode.prev) {
      const rotation =
        leaves2.find((d) => d.data.id === treeNode.prev).x - rotationMargin * 2;
      pdeg = rotation + rotationMargin;
      px = Math.sin(pdeg) * maxRad * radiusMultiplier;
      py = -Math.cos(pdeg) * maxRad * radiusMultiplier;
    }
    return (
      <g key={index} transform={`translate(${xPos} 0)`}>
        <circle
          cx={0}
          xy={0}
          r={maxRad * radiusMultiplier}
          style={{
            fill: 'none',
            stroke: 'black',
            opacity: 0.2,
            strokeDasharray: 2
          }}
        />
        <line
          x1={0}
          x2={0}
          y1={-(maxRad + 20) * radiusMultiplier}
          y2={(maxRad + 20) * radiusMultiplier}
          style={{ stroke: 'black', strokeDasharray: 2, opacity: 0.4 }}
        />
        <g transform={`rotate(${0})`}>
          {treeNode.next && radMult === 1 && (
            <g>
              <path
                className='connector'
                d={`M${nx},${ny}L${nx + Math.sin(ndeg) * 5},${ny +
                  -Math.cos(ndeg) * 5}A${maxRad * radiusMultiplier +
                  5},${maxRad * radiusMultiplier + 5},0,0,${
                  ny > 0 ? 0 : 1
                },${maxRad * radiusMultiplier + 5},0L${xMid - xPos},0`}
                style={{
                  stroke: '#3d8feb',
                  opacity: 0.5,
                  strokeDasharray: 0
                }}
              />
            </g>
          )}
          {treeNode.prev && radMult === 1 && (
            <g>
              <path
                className='connector'
                d={`M${px},${py}L${px + Math.sin(pdeg) * 5},${py +
                  -Math.cos(pdeg) * 5}A${maxRad * radiusMultiplier -
                  5},${maxRad * radiusMultiplier - 5},0,0,${
                  py > 0 ? 1 : 0
                },${-maxRad * radiusMultiplier - 5},0L${xPos - xMid},0`}
                style={{
                  stroke: '#3d8feb',
                  opacity: 0.5,
                  strokeDasharray: 0
                }}
              />
            </g>
          )}
          <g transform={`rotate(${(rotationMargin * 180) / Math.PI})`}>
            {paths.map((path, i) => {
              return (
                <path
                  markerEnd={
                    path.link.target.depth === 1 ? 'url(#arrow)' : null
                  }
                  key={i}
                  d={path.d}
                  className='radial-branch'
                  style={{
                    opacity: 0.5
                  }}
                />
              );
            })}
          </g>
          <g transform={`rotate(-${(rotationMargin * 180) / Math.PI})`}>
            {paths2.map((path, i) => {
              // console.log(path);
              return (
                <path
                  markerEnd={
                    path.link.target.depth === 1 ? 'url(#arrow2)' : null
                  }
                  key={i}
                  d={path.d}
                  style={{
                    opacity: 0.5,
                    stroke:
                      path.link.target.depth === 2
                        ? path.link.target.data.interest_group_category ===
                            'C' ||
                          path.link.target.data.interest_group_category === 'W'
                          ? 'black'
                          : 'black'
                        : 'black'
                  }}
                />
              );
            })}
            {points.map((leaf, i) => {
              const { depth } = leaf;

              return depth > 0 ? (
                <g
                  key={i}
                  transform={`rotate(${(leaf.x * 180) / Math.PI -
                    (90 - (rotationMargin * 2 * 180) / Math.PI)}), translate(${
                    leaf.y
                  },0)`}
                  onMouseOver={() => {
                    this.props.zoomed &&
                      this.props.handleHover(
                        { leaf: leaf.data, data },
                        false,
                        depth
                      );
                  }}
                >
                  <circle
                    cx={0}
                    cy={0}
                    r={2 * radiusMultiplier}
                    style={{
                      fill:
                        this.context.data.results
                          .map((d) => d.id)
                          .indexOf(leaf.data.id) !== -1
                          ? '#3d8feb'
                          : 'black'
                    }}
                  />
                  {zoomed && (
                    <g>
                      {depth === 1 && (
                        <text
                          className='com-name'
                          dy='.31em'
                          y={depth === 0 ? -12 : 0}
                          x={data.x > Math.PI ? -5 : 5}
                          textAnchor={
                            i === points.length - 1
                              ? 'middle'
                              : leaf.x > Math.PI
                              ? 'end'
                              : 'start'
                          }
                          style={{
                            fontSize: 8 + 'px',
                            strokeWidth: 4,
                            stroke: 'rgb(255, 255, 255)'
                          }}
                        >
                          {leaf.data.committee_name &&
                            this.concat(leaf.data.committee_name, 20)}
                        </text>
                      )}
                      <text
                        className='com-name'
                        dy='.31em'
                        y={depth === 0 ? -12 : 0}
                        x={(data.x > Math.PI ? -5 : 5) * (depth === 1 ? 1 : 2)}
                        textAnchor={
                          i === points.length - 1
                            ? 'middle'
                            : leaf.x > Math.PI
                            ? 'end'
                            : 'start'
                        }
                        style={{
                          fontSize:
                            depth === 1 ? 8 : points.length > 100 ? 3 : 8 + 'px'
                        }}
                      >
                        {leaf.data.committee_name &&
                          this.concat(leaf.data.committee_name, 20)}
                      </text>
                    </g>
                  )}
                </g>
              ) : null;
            })}
            {points2.map((leaf, i) => {
              const { depth } = leaf;
              return depth > 0 ? (
                <g
                  key={i}
                  transform={`rotate(${(leaf.x * 180) / Math.PI -
                    90}), translate(${leaf.y},0)`}
                  onMouseOver={() => {
                    this.props.zoomed &&
                      this.props.handleHover(
                        { leaf: leaf.data, data },
                        true,
                        depth
                      );
                  }}
                >
                  <circle
                    cx={0}
                    cy={0}
                    r={2 * radiusMultiplier}
                    style={{
                      fill:
                        this.context.data.results
                          .map((d) => d.id)
                          .indexOf(leaf.data.id) !== -1
                          ? '#3d8feb'
                          : 'black'
                    }}
                  />
                  {zoomed && (
                    <g transform={`rotate(180)`}>
                      {depth === 1 && (
                        <text
                          className='com-name'
                          dy='.31em'
                          y={depth === 0 ? -12 : 0}
                          x={data.x > Math.PI ? 5 : -5}
                          textAnchor={
                            i === points2.length - 1
                              ? 'middle'
                              : leaf.x > Math.PI
                              ? 'start'
                              : 'end'
                          }
                          style={{
                            stroke: 'rgb(255, 255, 255)',
                            strokeWidth: 3,
                            fontSize: 8 + 'px'
                          }}
                        >
                          {leaf.data.committee_name &&
                            this.concat(leaf.data.committee_name, 20)}
                        </text>
                      )}
                      <text
                        className='com-name'
                        dy='.31em'
                        y={depth === 0 ? -12 : 0}
                        x={(data.x > Math.PI ? 5 : -5) * (depth === 1 ? 1 : 2)}
                        textAnchor={
                          i === points2.length - 1
                            ? 'middle'
                            : leaf.x > Math.PI
                            ? 'start'
                            : 'end'
                        }
                        style={{
                          fontSize:
                            depth === 1
                              ? 8
                              : points2.length > 100
                              ? 3
                              : 8 + 'px'
                        }}
                      >
                        {leaf.data.committee_name &&
                          this.concat(leaf.data.committee_name, 20)}
                      </text>
                    </g>
                  )}
                </g>
              ) : null;
            })}
          </g>
        </g>
      </g>
    );
  }
}

RadialTidyTreeNode.contextType = Context;

// d={`M${px},${py}L${px - 10},${py - (10 * py) / px}A${maxRad *
//   radiusMultiplier -
//   10},${maxRad * radiusMultiplier - 10},0,0,${
//   py > 0 ? 1 : 0
// },${-maxRad * radiusMultiplier - 10},0L${xPos - xMid},0`}
